import { Link } from '@tanstack/react-router';
import { ClipboardCheck, Copy, MoreHorizontal, Pencil, Plus, Star, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorState, FeatureErrorBoundary } from '@nucleus-commerce/admin-kit/errors';
import { FormSection } from '@nucleus-commerce/admin-kit/forms';
import { PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import type { WorkflowTemplate } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@nucleus-commerce/ui/components/dropdown-menu';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@nucleus-commerce/ui/components/table';

import { ReadOnlyBanner } from '@/shared/store/read-only-banner';
import { useStore } from '@/shared/store/store-context';
import { useStoreEditable } from '@/shared/store/use-store-editable';

import { ConfirmDeleteDialog } from '../components/confirm-delete-dialog';
import { DuplicateTemplateDialog } from '../components/duplicate-template-dialog';
import {
  useDeleteWorkflowTemplate,
  useMakeDefaultWorkflowTemplate,
} from '../hooks/use-workflow-template-mutations';
import { useWorkflowTemplates } from '../hooks/use-workflow-templates';

function TemplateRow({
  storeId,
  template,
  editable,
  onDuplicate,
  onDelete,
}: {
  storeId: string;
  template: WorkflowTemplate;
  editable: boolean;
  onDuplicate: () => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation(['workflows', 'common']);
  const format = useFormat();
  const makeDefault = useMakeDefaultWorkflowTemplate(storeId);
  return (
    <TableRow aria-label={template.name}>
      <TableCell className="pl-6">
        <div className="flex items-center gap-2">
          <span className="font-medium">{template.name}</span>
          {template.isDefault ? <Badge variant="secondary">{t('default')}</Badge> : null}
        </div>
      </TableCell>
      <TableCell className="text-muted-foreground">
        {t('stageCount', { count: template.stages.length })}
      </TableCell>
      <TableCell className="text-muted-foreground text-sm">
        <span title={format.utc(template.updatedAt)}>{format.relative(template.updatedAt)}</span>
      </TableCell>
      <TableCell className="pr-6 text-right">
        <div className="flex items-center justify-end gap-1">
          <Button asChild variant="outline" size="sm">
            <Link
              to="/s/$storeId/settings/workflows/$templateId"
              params={{ storeId, templateId: template.id }}
              aria-label={t('actions.edit', { name: template.name })}
            >
              <Pencil aria-hidden />
              {t('common:actions.edit')}
            </Link>
          </Button>
          {editable ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={t('actions.more', { name: template.name })}
                >
                  <MoreHorizontal aria-hidden />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {template.isDefault ? null : (
                  <DropdownMenuItem
                    disabled={makeDefault.isPending}
                    onSelect={() => makeDefault.mutate(template.id)}
                  >
                    <Star aria-hidden />
                    {t('actions.makeDefault')}
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onSelect={onDuplicate}>
                  <Copy aria-hidden />
                  {t('actions.duplicate')}
                </DropdownMenuItem>
                <DropdownMenuItem variant="destructive" onSelect={onDelete}>
                  <Trash2 aria-hidden />
                  {t('actions.delete')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
        </div>
      </TableCell>
    </TableRow>
  );
}

function WorkflowTemplates() {
  const { t } = useTranslation(['workflows', 'common']);
  const store = useStore();
  const { editable, reason } = useStoreEditable({ permission: 'settings.write' });
  const query = useWorkflowTemplates(store.id);
  const remove = useDeleteWorkflowTemplate(store.id);
  const [duplicating, setDuplicating] = useState<WorkflowTemplate | null>(null);
  const [deleting, setDeleting] = useState<WorkflowTemplate | null>(null);
  const templates = query.data ?? [];

  return (
    <PageContent>
      <PageHeader
        title={t('title')}
        description={t('description', { store: store.name })}
        backTo={{ to: '/org/stores/$storeId', params: { storeId: store.id } }}
        backLabel={t('back')}
        actions={
          <>
            <Button asChild variant="outline" data-tour="workflows.list.qc-link">
              <Link to="/s/$storeId/settings/qc-checklists" params={{ storeId: store.id }}>
                <ClipboardCheck aria-hidden />
                {t('qcLink')}
              </Link>
            </Button>
            {editable ? (
              <Button asChild data-tour="workflows.list.new">
                <Link to="/s/$storeId/settings/workflows/new" params={{ storeId: store.id }}>
                  <Plus aria-hidden />
                  {t('new')}
                </Link>
              </Button>
            ) : null}
          </>
        }
      />
      <ReadOnlyBanner reason={reason} />
      <FormSection title={t('listLabel')} className="max-w-4xl">
        {query.isError ? (
          <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        ) : query.isPending ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : templates.length === 0 ? (
          <p className="text-muted-foreground text-sm" data-tour="workflows.list.empty">
            {t('empty')}
          </p>
        ) : (
          <div className="-mx-6" data-tour="workflows.list.table">
            <Table aria-label={t('listLabel')}>
              <TableHeader>
                <TableRow>
                  <TableHead className="pl-6">{t('columns.name')}</TableHead>
                  <TableHead>{t('columns.stages')}</TableHead>
                  <TableHead>{t('columns.updated')}</TableHead>
                  <TableHead className="pr-6 text-right" data-tour="workflows.list.row-menu">
                    {t('columns.actions')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {templates.map((template) => (
                  <TemplateRow
                    key={template.id}
                    storeId={store.id}
                    template={template}
                    editable={editable}
                    onDuplicate={() => setDuplicating(template)}
                    onDelete={() => {
                      remove.reset();
                      setDeleting(template);
                    }}
                  />
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </FormSection>
      <DuplicateTemplateDialog
        storeId={store.id}
        template={duplicating}
        onOpenChange={(open) => {
          if (!open) setDuplicating(null);
        }}
      />
      <ConfirmDeleteDialog
        open={deleting !== null}
        title={t('delete.title', { name: deleting?.name ?? '' })}
        description={t('delete.description')}
        confirmLabel={t('delete.confirm')}
        inUseMessage={t('delete.inUse')}
        isPending={remove.isPending}
        error={remove.error}
        onConfirm={() => {
          if (deleting) remove.mutate(deleting.id, { onSuccess: () => setDeleting(null) });
        }}
        onOpenChange={(open) => {
          if (!open) setDeleting(null);
        }}
      />
    </PageContent>
  );
}

export function WorkflowTemplatesPage() {
  return (
    <FeatureErrorBoundary name="workflows.list">
      <WorkflowTemplates />
    </FeatureErrorBoundary>
  );
}
